import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box } from '@mui/material';
import StyledCard from '../../../../components/common/StyledCard';
import { OnlyFansSubscriberHostessAutoReplyFreeloaders } from '../../../../types/hostessAutoReplyFreeloaders';
import FormActionToTakeSelect from './FormActionToTakeSelect';
import FormAutoUnrestrictWhenSpendingCheckbox from './FormAutoUnrestrictWhenSpendingCheckbox';
import FormDelayReplyCheckbox from './FormDelayReplyCheckbox';
import FormFollowingThesholdEnabledCheckbox from './FormFollowingThesholdEnabledCheckbox';
import FormFollowingThresholdPeriodSelect from './FormFollowingThresholdPeriodSelect';
import FormFollowThresholdAmount from './FormFollowThresholdAmount';
import FormHistoryTtlDays from './FormHistoryTtlDays';
import FormHistoryTtlEnabledCheckbox from './FormHistoryTtlEnabledCheckbox';
import FormRepeatRepliesCheckbox from './FormRepeatRepliesCheckbox';
import FormSkipCreatorsCheckbox from './FormSkipCreatorsCheckbox';
import FormSpentThresholdAmount from './FormSpentThresholdAmount';
import SettingRow from './SettingRow';
import SettingsSectionHeader from './SettingsSectionHeader';

const AutoReplySettingsCard = (props: {
    formData: OnlyFansSubscriberHostessAutoReplyFreeloaders;
    setFormData: (data: OnlyFansSubscriberHostessAutoReplyFreeloaders) => void;
    autoReplyFreeloaders: OnlyFansSubscriberHostessAutoReplyFreeloaders | null;
    followingThresholdEnabled: boolean;
    setFollowingThresholdEnabled: (followingThresholdEnabled: boolean) => void;
    followingThresholdAmountString: string;
    setFollowingThresholdAmountString: (followingThresholdAmountString: string) => void;
    historyTtlEnabled: boolean;
    setHistoryTtlEnabled: (historyTTLEnabled: boolean) => void;
    historyTtlDaysString: string;
    setHistoryTtlDaysString: (historyTTLAmountString: string) => void;
}) => {
    const {
        formData,
        setFormData,
        autoReplyFreeloaders,
        followingThresholdEnabled,
        setFollowingThresholdEnabled,
        followingThresholdAmountString,
        setFollowingThresholdAmountString,
        historyTtlEnabled,
        setHistoryTtlEnabled,
        historyTtlDaysString,
        setHistoryTtlDaysString,
    } = props;

    return (
        <>
            <StyledCard>
                <SettingsSectionHeader icon={PersonSearchOutlinedIcon} title="Whom to Target" />

                <SettingRow
                    title="Spent Threshold"
                    description="Any amount below this one will trigger the auto-reply"
                    control={
                        <Box sx={{ width: 120 }}>
                            <FormSpentThresholdAmount
                                formData={formData}
                                setFormData={setFormData}
                                autoReplyFreeloaders={autoReplyFreeloaders}
                            />
                        </Box>
                    }
                />

                <SettingRow
                    title="Grace Period"
                    description="Freeloaders who have followed you within this time will be skipped"
                    wide
                    last
                    control={
                        <>
                            <FormFollowingThesholdEnabledCheckbox
                                followingThresholdEnabled={followingThresholdEnabled}
                                setFollowingThresholdEnabled={setFollowingThresholdEnabled}
                                setFollowingThresholdAmountString={setFollowingThresholdAmountString}
                                formData={formData}
                                setFormData={setFormData}
                            />
                            <FormFollowThresholdAmount
                                followingThresholdEnabled={followingThresholdEnabled}
                                followingThresholdAmountString={followingThresholdAmountString}
                                setFollowingThresholdAmountString={setFollowingThresholdAmountString}
                                autoReplyFreeloaders={autoReplyFreeloaders}
                                formData={formData}
                                setFormData={setFormData}
                            />
                            {/* Fills the remaining row width on phones; fixed width beside the text on larger screens */}
                            <Box sx={{ width: { sm: 130 }, flexGrow: { xs: 1, sm: 0 } }}>
                                <FormFollowingThresholdPeriodSelect
                                    followingThresholdEnabled={followingThresholdEnabled}
                                    formData={formData}
                                    setFormData={setFormData}
                                />
                            </Box>
                        </>
                    }
                />
            </StyledCard>

            <StyledCard>
                <SettingsSectionHeader icon={PeopleAltOutlinedIcon} title="Whom to Skip" />

                <SettingRow
                    title="Skip Creators?"
                    description="If checked, auto-reply will not reply to creators"
                    last
                    control={<FormSkipCreatorsCheckbox formData={formData} setFormData={setFormData} />}
                />
            </StyledCard>

            <StyledCard>
                <SettingsSectionHeader icon={SettingsOutlinedIcon} title="Action to Take" />

                <SettingRow
                    title="Action to Take on Last Reply"
                    description="What to do when the freeloader has received the maximum number of strikes (the last reply)"
                    wide
                    control={
                        <Box sx={{ width: { xs: '100%', sm: 140 } }}>
                            <FormActionToTakeSelect formData={formData} setFormData={setFormData} />
                        </Box>
                    }
                />

                <SettingRow
                    title="Auto Unmute/Unrestrict When Spending?"
                    description="If checked, fans will be unmuted or unrestricted as necessary when they spend above the threshold"
                    last
                    control={<FormAutoUnrestrictWhenSpendingCheckbox formData={formData} setFormData={setFormData} />}
                />
            </StyledCard>

            <StyledCard>
                <SettingsSectionHeader icon={EmailOutlinedIcon} title="Message Settings" />

                <SettingRow
                    title="Delay Before Sending Reply?"
                    description="If checked, the reply will be sent a random 1-2 minutes after the fan sent the message."
                    control={<FormDelayReplyCheckbox formData={formData} setFormData={setFormData} />}
                />

                <SettingRow
                    title="Repeat Replies When Script Finished?"
                    description="If checked, the script will be repeated from the start when the fan has received the last reply and they message again."
                    control={<FormRepeatRepliesCheckbox formData={formData} setFormData={setFormData} />}
                />

                <SettingRow
                    title="Repeat Script After X Days?"
                    description="If checked, the script will be repeated from the start when the fan messages after the specified number of days."
                    last
                    control={
                        <>
                            <FormHistoryTtlEnabledCheckbox
                                historyTtlEnabled={historyTtlEnabled}
                                setHistoryTtlEnabled={setHistoryTtlEnabled}
                                setHistoryTtlDaysString={setHistoryTtlDaysString}
                                formData={formData}
                                setFormData={setFormData}
                            />
                            <FormHistoryTtlDays
                                historyTtlEnabled={historyTtlEnabled}
                                historyTtlDaysString={historyTtlDaysString}
                                setHistoryTtlDaysString={setHistoryTtlDaysString}
                                autoReplyFreeloaders={autoReplyFreeloaders}
                                formData={formData}
                                setFormData={setFormData}
                            />
                        </>
                    }
                />
            </StyledCard>
        </>
    );
};

export default AutoReplySettingsCard;
